import {
	type AclTranslation,
	DateObject,
	type PostSocialUserInterestsPayload,
	sessionAtom,
	type SocialInterest,
	type SocialInterestType,
	useLocale,
	useSettings
} from '@capsicum/common';
import { useMe } from '@capsicum/socios-core';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useGetInterests, useGetUserInterests, useSendInterests } from '../data-access/queries';

export const useInterestsFlow = () => {
	const { locale } = useLocale();
	const isLoggedIn = !!useAtomValue(sessionAtom)?.authToken;
	const [interests, setInterests] = useState<Array<string>>([]);
	const [isVisible, setVisibility] = useState<boolean>(false);

	const { isMeLoading, me } = useMe({ enabled: isLoggedIn });
	const { mutateAsync: sendInterests } = useSendInterests({ enabled: isLoggedIn });
	const { data: userInterests, isLoading: areUserInterestsLoading } = useGetUserInterests({
		enabled: isLoggedIn && isVisible
	});

	const { data: categories, isLoading: areCategoriesLoading } = useGetInterests(
		{ enabled: isLoggedIn && isVisible },
		{
			'details[]': ['interestTranslations', 'interestTypeTranslations'],
			'locales[]': [locale]
		}
	);

	const isLoading = areCategoriesLoading || areUserInterestsLoading;

	const {
		areSettingsLoading,
		settings: { interestsPromptEligibilityDate, isInterestsScreenEnabled }
	} = useSettings();

	const isNextStepEnabled = interests.length > 0;
	const isCheckLoading = isLoggedIn && (isMeLoading || areSettingsLoading || areUserInterestsLoading);

	const userRegistrationDate = useMemo(() => {
		return !me.createdAt ? null : new DateObject(me.createdAt);
	}, [me.createdAt]);

	const eligibilityDate = useMemo(() => {
		return new DateObject(interestsPromptEligibilityDate);
	}, [interestsPromptEligibilityDate]);

	const onSetChipCallback = useCallback(
		(interest: string) => {
			const position = interests.indexOf(interest);

			if (position !== -1) {
				const newInterests = [...interests];

				newInterests.splice(position, 1);
				setInterests(newInterests);

				return;
			}

			setInterests([...new Set([...interests, interest])]);
		},
		[interests, setInterests]
	);

	const onContinue = useCallback(() => {
		const payloadInterests: PostSocialUserInterestsPayload = interests.map(interest => ({
			interestId: interest
		}));

		sendInterests(payloadInterests);
		setVisibility(false);
	}, [setVisibility, interests]);

	const findTranslationKey = (key: string, interest: SocialInterest | SocialInterestType) => {
		const translations = interest?.translations as Array<AclTranslation>;

		return (translations?.find((translation: AclTranslation) => translation.field === key)?.value ??
			interest[key as keyof typeof interest]) as string;
	};

	useEffect(() => {
		setVisibility(
			isCheckLoading
				? false
				: isInterestsScreenEnabled &&
						(userInterests ?? []).length === 0 &&
						eligibilityDate.isBefore(userRegistrationDate)
		);
	}, [userInterests, isCheckLoading, eligibilityDate, userRegistrationDate, isInterestsScreenEnabled]);

	return {
		categories,
		findTranslationKey,
		isLoading,
		isNextStepEnabled,
		isVisible,
		onContinue,
		onSetChipCallback
	};
};
